import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';
import { ROLES_CRUD } from '../constants/roles';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiereCambioPassword, setRequiereCambioPassword] = useState(false);

  // Función helper para formatear errores de validación del backend
  const formatErrorMessage = (error) => {
    if (!error.response?.data) return error.message || 'Error desconocido';
    
    const errorData = error.response.data;
    
    // Si detail es un array de errores de validación de Pydantic
    if (Array.isArray(errorData.detail)) {
      return errorData.detail.map(err => {
        // Extraer el campo del array loc (ej: ["body", "password"] -> "password")
        const field = err.loc ? err.loc[err.loc.length - 1] : 'Campo';
        const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
        return `${fieldName}: ${err.msg}`;
      }).join('. ');
    }
    
    // Si detail es un string simple
    if (typeof errorData.detail === 'string') {
      return errorData.detail;
    }
    
    // Si detail es un objeto con msg
    if (errorData.detail?.msg) {
      return errorData.detail.msg;
    }
    
    return error.message || 'Error al procesar la solicitud';
  };

  // Verificar autenticación al cargar la app
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    setIsLoading(true);
    try {
      if (authService.isAuthenticated()) {
        // Intentar obtener usuario actual desde el servidor (siempre actualizado)
        const userData = await authService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
        setRequiereCambioPassword(userData.requiereCambioPassword ?? false);
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Error al verificar autenticación:', error);
      setIsAuthenticated(false);
      setUser(null);
      setRequiereCambioPassword(false);
      authService.logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authService.login(email, password);
      const userData = response.user;
      const requiere =
        typeof response.requiere_cambio_password === 'boolean'
          ? response.requiere_cambio_password
          : (userData.requiereCambioPassword ?? false);
      setUser({
        ...userData,
        requiereCambioPassword: requiere,
      });
      setIsAuthenticated(true);
      setRequiereCambioPassword(requiere);
      return {
        ...userData,
        requiereCambioPassword: requiere,
      };
    } catch (error) {
      const errorMessage = formatErrorMessage(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    setError(null);
    setIsLoading(true);
    try {
      const newUser = await authService.register(userData);
      return newUser;
    } catch (error) {
      const errorMessage = formatErrorMessage(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setRequiereCambioPassword(false);
      setError(null);
    }
  };

  // Función para verificar si el usuario puede crear/editar
  const canCreateEdit = () => {
    if (!user) return false;
    return ROLES_CRUD.includes(user.rol);
  };

  const isSuperAdmin = () => {
    if (!user) return false;
    return user.rol === 'SuperAdmin';
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    register,
    checkAuth,
    canCreateEdit,
    isSuperAdmin,
    requiereCambioPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

export default AuthContext;
